import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from '@/components/common/toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Backend unified response envelope. */
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T | null;
  timestamp: string;
}

/** Shape of the raw response body before unwrapping. */
type RawResponse = ApiResponse | unknown;

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const request = axios.create({
  baseURL: '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Helper: detect unified response envelope
// ---------------------------------------------------------------------------

function isApiResponse(body: unknown): body is ApiResponse {
  return (
    body !== null &&
    typeof body === 'object' &&
    'code' in body &&
    typeof (body as ApiResponse).code === 'number' &&
    'message' in body &&
    'data' in body
  );
}

// ---------------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------------

request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.token = token;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — unified unwrapping + error handling
// ---------------------------------------------------------------------------

request.interceptors.response.use(
  (response: AxiosResponse<RawResponse>) => {
    const body = response.data;

    // Non-unified response — pass through as-is
    if (!isApiResponse(body)) {
      return response;
    }

    // Business error: code !== 0
    if (body.code !== 0) {
      const err = new AxiosError(
        body.message || 'Request failed',
        'BUSINESS_ERROR',
        response.config,
        response.request,
        response,
      );
      return Promise.reject(err);
    }

    // Success: unwrap the data field
    response.data = body.data;
    return response;
  },

  (error: AxiosError<RawResponse>) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const body = error.response?.data;
    const serverMessage = isApiResponse(body) ? body.message : '';

    const status = error.response?.status;

    // 401 — not authenticated
    if (status === 401) {
      toast.error(serverMessage || '会话已过期，请重新登录。');
      setTimeout(() => {
        const currentPath = window.location.hash.replace(/^#/, "");
        if (!currentPath.includes("login-manual") && !currentPath.includes("admin-login")) {
          window.location.hash = '#/login-manual';
        }
      }, 1500);
      return Promise.reject(error);
    }

    // 403 — forbidden
    if (status === 403) {
      toast.error(serverMessage || '您没有权限执行此操作。');
      return Promise.reject(error);
    }

    // 404
    if (status === 404) {
      toast.error(serverMessage || '请求的资源不存在。');
      return Promise.reject(error);
    }

    // 500+
    if (status && status >= 500) {
      toast.error(serverMessage || '服务器错误，请稍后重试。');
      return Promise.reject(error);
    }

    // Network error (no response)
    if (error.code === 'ECONNABORTED') {
      toast.error('请求超时，请检查网络后重试。');
      return Promise.reject(error);
    }

    if (!error.response) {
      toast.error('网络异常，请检查网络连接。');
      return Promise.reject(error);
    }

    // Other errors
    toast.error(serverMessage || error.message || '请求失败。');
    return Promise.reject(error);
  },
);

export default request;
